/**
 * Publish the list on screen (filters, search, sort, page, total, view, ticked rows,
 * side-panel record) for page-aware AI. Called ONCE in DocTypeListPageShell, so every
 * doctype list is covered without per-page code. Cleared on unmount; keyed by the
 * shell's filterStoreKey + pathname so another page never reports a stale list.
 */

import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import type { PaginationState, RowSelectionState, SortingState } from "@tanstack/react-table";
import type { FilterDescriptor } from "@/lib/erp/filterTypes";
import { pageContextStore } from "./pageContextStore";
import { PAGE_CONTEXT_LIMITS, type ListContext } from "./types";

export interface PublishListContextArgs {
  doctype: string;
  /** The shell's filterStoreKey (publisher key) */
  listKey: string;
  filters: FilterDescriptor[];
  search: string;
  sorting: SortingState;
  pagination: PaginationState;
  total?: number;
  viewMode?: string;
  rowSelection: RowSelectionState;
  /** Record open in the side panel (?detail=) */
  openDetail?: string | null;
}

export function buildListContext(args: PublishListContextArgs): ListContext {
  const selected = Object.entries(args.rowSelection)
    .filter(([, isSelected]) => isSelected)
    .map(([id]) => id);
  const sort = args.sorting[0];
  const search = args.search.trim();
  return {
    doctype: args.doctype,
    filters: args.filters.slice(0, PAGE_CONTEXT_LIMITS.maxListItems).map((f) => ({
      field: f.childDoctype ? `${f.childDoctype}.${f.field}` : f.field,
      operator: f.operator,
      value: f.value,
    })),
    ...(search ? { search } : {}),
    ...(sort ? { sort: { field: sort.id, desc: !!sort.desc } } : {}),
    page: args.pagination.pageIndex + 1,
    pageSize: args.pagination.pageSize,
    ...(typeof args.total === "number" ? { total: args.total } : {}),
    ...(args.viewMode ? { viewMode: args.viewMode } : {}),
    selectedIds: selected.slice(0, PAGE_CONTEXT_LIMITS.maxSelectedIds),
    selectedCount: selected.length,
    ...(args.openDetail ? { openDetail: args.openDetail } : {}),
  };
}

export function usePublishListContext(args: PublishListContextArgs): void {
  const { pathname } = useLocation();
  const { listKey } = args;
  const data = useMemo(
    () => buildListContext(args),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebuilt only when a reported value changes
    [
      args.doctype,
      args.filters,
      args.search,
      args.sorting,
      args.pagination.pageIndex,
      args.pagination.pageSize,
      args.total,
      args.viewMode,
      args.rowSelection,
      args.openDetail,
    ],
  );

  useEffect(() => {
    pageContextStore.getState().setList({ key: listKey, path: pathname, data });
  }, [listKey, pathname, data]);

  useEffect(() => {
    return () => pageContextStore.getState().clearList(listKey);
  }, [listKey]);
}
